import { ButtonV2, InputV2, TextArea } from '@cimplico/components';
import { useNavigate } from 'react-router-dom';
import { useCreateCustomWorksheetTemplate } from '../../queries/use-create-custom-worksheet-template';
import { toastNotification } from '../../components/toast/Toast';
import { Controller, useForm } from 'react-hook-form';
import { EntityTypeEnum } from '../../types/entity';
import EntityTypeSelect from '../../components/entity-type-select/EntityTypeSelect';
import { CWTBehaviourEnum } from '../../types/custom-worksheet-v2';
import AccountToggle from '../custom-worksheet-template/details-form-components/AccountToggle';
import AccountBehaviourRadio from '../custom-worksheet-template/details-form-components/AccountBehaviourRadio';
import OtherScheduleToggle from './OtherScheduleToggle';
import BehaviourRadioButton from './BehaviourRadioButton';
import { useCreateCalculationTemplate } from '../../queries/resources/calculation-templates';

type CreateFormData = {
    name: string;
    description: string;
    tags: string[];
    entityTypes: EntityTypeEnum[];
    allowAsSchedule: boolean;
    behaviour: CWTBehaviourEnum | null;
};

function NewCustomWorksheetTemplateForm(): JSX.Element {
    const navigate = useNavigate();
    const form = useForm<CreateFormData>({
        defaultValues: {
            entityTypes: [],
            allowAsSchedule: true,
            behaviour: CWTBehaviourEnum.SHARE,
            tags: [],
        },
    });

    const createQuery = useCreateCalculationTemplate();

    function handleSubmit(data: CreateFormData): void {
        const _name = data.name.trim();
        const _tags = data.tags.map((t) => t.trim()).filter((t) => t);
        const _description = data.description?.trim() || null;
        const allowAsSchedule = data.allowAsSchedule;
        const behaviour = data.behaviour;

        createQuery.mutate(
            { name: _name, tags: _tags, description: _description, entityTypes: data.entityTypes, allowAsSchedule, behaviour },
            {
                onSuccess: (res) => {
                    toastNotification('success', 'Successfully created calculation template');
                    navigate(`../${res.template.id}`);
                },
                onError: (err) => {
                    const formErrors: Record<string, string[]> = err.response?.data;
                    Object.entries(formErrors).forEach((entry) => {
                        const k = entry[0] as keyof CreateFormData;
                        form.setError(k, { message: entry[1][0] });
                    });
                },
            }
        );
    }

    return (
        <form className="flex flex-col gap-8" onSubmit={form.handleSubmit(handleSubmit)}>
            {/* Name */}
            <Controller
                name="name"
                control={form.control}
                rules={{ required: 'Name is required' }}
                render={(render): JSX.Element => (
                    <InputV2
                        type="text"
                        onChange={render.field.onChange}
                        title="Name"
                        autoFocus
                        characterLimit={50}
                        errorMessage={render.fieldState.error?.message}
                        placeholder="e.g. Capital Gains Tax Rec"
                    />
                )}
            />
            {/* Tag */}
            <Controller
                name="tags"
                control={form.control}
                rules={{ required: 'Tag is required' }}
                render={(render): JSX.Element => (
                    <InputV2
                        characterLimit={50}
                        type="text"
                        onChange={(e): void => render.field.onChange(e.split(','))}
                        title="Tag"
                        errorMessage={render.fieldState.error?.message}
                        placeholder="Enter a tag..."
                    />
                )}
            />
            {/* Account */}
            <OtherScheduleToggle control={form.control} name="allowAsSchedule" />
            {/* Account behaviour */}
            <BehaviourRadioButton control={form.control} name="behaviour" />

            {/* Entity Types */}
            <Controller
                name="entityTypes"
                control={form.control}
                render={(render): JSX.Element => (
                    <EntityTypeSelect
                        isMulti
                        title="Entity Types"
                        isOptional
                        onChange={render.field.onChange}
                        defaultEntityType={render.field.value}
                    />
                )}
            />

            {/* Description */}
            <Controller
                name="description"
                control={form.control}
                rules={{ required: false }}
                render={(render): JSX.Element => (
                    <TextArea
                        onChange={render.field.onChange}
                        title="Description"
                        characterLimit={250}
                        placeholder="Enter a description..."
                        isOptional
                    />
                )}
            />

            <div className="flex justify-end gap-4">
                <ButtonV2 size="sm" type="hollow" label="Cancel" onClick={(): void => navigate(`..`)} />
                <ButtonV2 size="sm" type="primary" label="Create" isDisabled={createQuery.isLoading} isSubmit />
            </div>
        </form>
    );
}

export default NewCustomWorksheetTemplateForm;
